import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Chip, Typography, Divider, Alert, IconButton } from '@mui/material';
import { searchMembersWithSubMembers } from '../../controllers/memberManagementController';
import { addParticipant, addGuest } from '../../controllers/clubEventsController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const AddEventParticipantDialog = ({ open, onClose, eventId, event, onAdded }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  
  // Guest list state - birden fazla misafir eklenebilir
  const [guestsList, setGuestsList] = useState([]);
  
  // Guest form state - yeni misafir eklemek için
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (!open) return;
    if (!query || query.length < 2) { setOptions([]); return; }
    let active = true;
    setLoading(true);
    searchMembersWithSubMembers(query)
      .then((data) => { if (active) setOptions(data || []); })
      .catch(() => { if (active) setOptions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, query]);

  
  // Seçilen üyenin misafir bilgilerini bul
  const selectedParticipant = useMemo(() => {
    if (!selected || !event?.participants || !Array.isArray(event.participants)) return null;
    const personId = selected?.id || selected?.Id;
    return event.participants.find((p) => (p.personId || p.PersonId) === personId);
  }, [selected, event?.participants]);

  // Misafir kontenjan kontrolü
  const guestCapacity = event?.guestCapacity ?? 0;
  const currentGuestCount = selectedParticipant?.guestCount ?? selectedParticipant?.GuestCount ?? 0;
  const totalGuestsToAdd = guestsList.length;
  const remainingGuestCapacity = guestCapacity - currentGuestCount - totalGuestsToAdd;
  
  const canAddGuestToList = useMemo(() => {
    if (!eventId || !selected) return false;
    if (!event?.allowGuests) return false;
    if (guestCapacity <= 0) return false;
    if (remainingGuestCapacity <= 0) return false;
    return Boolean(
      guestForm.firstName.trim() &&
      guestForm.lastName.trim()
    );
  }, [eventId, selected, event?.allowGuests, guestCapacity, remainingGuestCapacity, guestForm]);
  
  const canAddParticipant = useMemo(() => {
    return Boolean(eventId && (selected?.id || selected?.Id));
  }, [eventId, selected]);

  const allowGuests = event?.allowGuests === true;

  const handleAddGuestToList = () => {
    if (!canAddGuestToList) return;
    setGuestsList((prev) => [
      ...prev,
      {
        firstName: guestForm.firstName.trim(),
        lastName: guestForm.lastName.trim(),
        phoneNumber: guestForm.phoneNumber.trim() || null,
      },
    ]);
    setGuestForm({
      firstName: '',
      lastName: '',
      phoneNumber: '',
    });
  };

  const handleRemoveGuestFromList = (index) => {
    setGuestsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = async () => {
    if (!canAddParticipant) return;
    setErrorText('');
    setSubmitting(true);
    try {
      const personId = selected?.id || selected?.Id;
      if (!personId) {
        setErrorText('Seçilen kişi bilgisinde kimlik bulunamadı.');
        setSubmitting(false);
        return;
      }
      
      // PersonType'ı search sonucundan al
      const personType = typeof selected?.type === 'number' ? selected.type : (typeof selected?.Type === 'number' ? selected.Type : 0);
      
      // Önce üyeyi ekle
      await addParticipant({
        EventId: eventId,
        PersonId: personId,
        Type: personType,
      });
      
      // Üye başarıyla eklendiyse, misafirleri ekle (kullanıcıya fark ettirmeden)
      if (guestsList.length > 0 && allowGuests) {
        // Her misafiri ayrı ayrı ekle, birinde hata olsa bile diğerleri eklenmeye devam etsin
        const guestResults = [];
        for (const guest of guestsList) {
          try {
            await addGuest({
              EventId: eventId,
              HostPersonId: personId,
              HostType: personType,
              FirstName: guest.firstName,
              LastName: guest.lastName,
              PhoneNumber: guest.phoneNumber,
            });
            guestResults.push({ success: true, guest });
          } catch (e) {
            guestResults.push({ success: false, guest, error: e?.message || 'Misafir eklenemedi' });
            // Hata olsa bile devam et, diğer misafirleri eklemeye çalış
          }
        }
        
        // Eğer bazı misafirler eklenemediyse kullanıcıya bilgi ver
        const failedGuests = guestResults.filter((r) => !r.success);
        if (failedGuests.length > 0) {
          const failedNames = failedGuests.map((r) => `${r.guest.firstName} ${r.guest.lastName}`).join(', ');
          setErrorText(`${failedGuests.length} misafir eklenemedi: ${failedNames}`);
        }
      }
      
      // Formu temizle ve event'i yenile
      setSelected(null);
      setQuery('');
      setOptions([]);
      setGuestsList([]);
      setGuestForm({
        firstName: '',
        lastName: '',
        phoneNumber: '',
      });
      
      await onAdded?.();
    } catch (e) {
      setErrorText(e?.message || 'Katılımcı eklenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setQuery('');
      setOptions([]);
      setGuestsList([]);
      setGuestForm({
        firstName: '',
        lastName: '',
        phoneNumber: '',
      });
      setErrorText('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Katılımcı Ekle</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Üye Seçimi */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Autocomplete
              sx={{ flex: 1, minWidth: 320 }}
              options={options}
              filterOptions={(x) => x}
              isOptionEqualToValue={(o, v) =>
                (o?.id || o?.Id) === (v?.id || v?.Id)
              }
              getOptionLabel={(o) => o.fullName || o.FullName || ''}
              value={selected}
              onChange={(e, v) => {
                setSelected(v);
                // Üye değiştiğinde misafir listesini ve formunu temizle
                setGuestsList([]);
                setGuestForm({
                  firstName: '',
                  lastName: '',
                  phoneNumber: '',
                });
              }}
              onInputChange={(e, v) => setQuery(v)}
              loading={loading}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl)} sx={{ width: 28, height: 28 }} />
                  {option.fullName || option.FullName}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Üye ara"
                  placeholder="İsim yazın..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText={errorText && !selected ? errorText : ''}
                  error={Boolean(errorText && !selected)}
                />
              )}
            />
            {/* Seçilen kişi özeti */}
            {selected && (
              <Stack sx={{ minWidth: 260 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar src={resolveGroupsAssetUrl(selected.profilePictureUrl || selected.ProfilePictureUrl)} sx={{ width: 28, height: 28 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.fullName || selected.FullName}</Typography>
                  <Chip label={(typeof selected.type === 'number' ? selected.type : selected.Type) === 0 ? 'Member' : 'SubMember'} size="small" />
                </Stack>
              </Stack>
            )}
            <Button variant="contained" onClick={handleAdd} disabled={!canAddParticipant || submitting}>
              {submitting ? 'Ekleniyor...' : 'Üye Ekle'}
            </Button>
          </Stack>

          {/* Misafir Ekleme Bölümü - Sadece üye seçildiyse ve allowGuests true ise göster */}
          {selected && allowGuests && (
            <>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                Misafir Ekle
              </Typography>
              
              {/* Kontenjan Bilgisi */}
              {guestCapacity > 0 && (
                <Alert severity={remainingGuestCapacity > 0 ? 'info' : 'warning'} sx={{ py: 0.5 }}>
                  <Typography variant="body2">
                    {remainingGuestCapacity > 0 ? (
                      <>
                        Bu üye için <strong>{remainingGuestCapacity}</strong> misafir daha eklenebilir. 
                        (Mevcut: {currentGuestCount}, Eklenen: {totalGuestsToAdd}, Toplam: {currentGuestCount + totalGuestsToAdd}/{guestCapacity})
                      </>
                    ) : (
                      <>
                        Bu üye için misafir kontenjanı dolmuş. (Mevcut: {currentGuestCount}, Eklenen: {totalGuestsToAdd}, Toplam: {currentGuestCount + totalGuestsToAdd}/{guestCapacity})
                      </>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Misafir Listesi */}
              {guestsList.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Eklenen Misafirler ({guestsList.length})
                  </Typography>
                  <Stack spacing={1}>
                    {guestsList.map((guest, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2">
                          {guest.firstName} {guest.lastName}
                          {guest.phoneNumber && ` - ${guest.phoneNumber}`}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveGuestFromList(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Misafir Formu */}
              {remainingGuestCapacity > 0 ? (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Misafir Adı"
                      fullWidth
                      required
                      value={guestForm.firstName}
                      onChange={(e) => setGuestForm((f) => ({ ...f, firstName: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                      label="Misafir Soyadı"
                      fullWidth
                      required
                      value={guestForm.lastName}
                      onChange={(e) => setGuestForm((f) => ({ ...f, lastName: e.target.value }))}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Stack>
                  <TextField
                    label="Telefon (Opsiyonel)"
                    fullWidth
                    value={guestForm.phoneNumber}
                    onChange={(e) => setGuestForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                    inputProps={{ maxLength: 20 }}
                    placeholder="+90..."
                  />
                  {errorText && (
                    <Typography variant="body2" color="error" sx={{ fontSize: 13 }}>
                      {errorText}
                    </Typography>
                  )}
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={handleAddGuestToList} 
                    disabled={!canAddGuestToList || submitting} 
                    fullWidth
                  >
                    Listeye Ekle
                  </Button>
                </Stack>
              ) : (
                <Alert severity="warning">
                  Kontenjan dolduğu için daha fazla misafir eklenemez.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEventParticipantDialog;


